'use client';

import Image from 'next/image';
import { useWriteContract } from '@ywwwtseng/wallet-kit/evm';
import BigNumber from 'bignumber.js';
import { Button, useI18n, useNavigate, toast, useMutation } from '@ywwwtseng/react-kit';
import { Typography } from '@ywwwtseng/tma-react-kit';
import type { Investment } from '@/hooks/useInvestments';
import * as utils from '@/utils';
import { MOONINI_DECIMALS, MOONINI_LOCK_ADDRESS } from '@/constants';
import MOONINI_LOCK_ABI from '@/assets/abi/MooniniLock.abi.json';
import { useInvestmentTeamLock } from '@/hooks/useInvestmentTeamLock';
import { HandCoins } from 'lucide-react';


interface InvestmentCardProps {
  investment: Investment;
  index: number;
  assignor?: string; // 当前用户的地址（assignor）
  pendingRightAssignments?: Array<{
    id: string;
    assign_id: string;
    investment_index: number;
    assignor: string;
  }>; // 从父组件传入的 pending right assignments
  onCancelSuccess: () => void;
}

export function InvestmentCard({ investment, index, assignor, pendingRightAssignments, onCancelSuccess }: InvestmentCardProps) {
  const { t } = useI18n();
  const navigate = useNavigate();

  const totalClaimable = new BigNumber(investment.totalClaimableAmount).minus(investment.assignedAmount);
  const claimed = new BigNumber(investment.claimedAmount).minus(investment.assignedAmount);
  const remaining = totalClaimable.minus(claimed);
  const formattedTotal = utils.token.displayAmountWithDecimals(totalClaimable.toFixed(), MOONINI_DECIMALS);
  const formattedClaimed = utils.token.displayAmountWithDecimals(claimed.toFixed(), MOONINI_DECIMALS);
  const formattedRemaining = utils.token.displayAmountWithDecimals(remaining.toFixed(), MOONINI_DECIMALS);
  const progress = totalClaimable.gt(0) ? claimed.dividedBy(totalClaimable).multipliedBy(100).toNumber() : 0;
  const startTime = Number(investment.investmentStartTime);
  const endTime = Number(investment.investmentEndTime);
  const startDate = new Date(startTime * 1000);
  const endDate = new Date(endTime * 1000);
  const isTeamLock = useInvestmentTeamLock(investment);

  // 从 pending 列表中找到当前 investment 对应的 right assignment
  const currentRightAssignment = pendingRightAssignments?.find(
    (ra) =>
      ra.investment_index === index && assignor && ra.assignor.toLowerCase() === assignor.toLowerCase()
  ) || null;

  // Mutation 用于更新 right assignment 状态
  const { mutate: cancelRightAssignment, isLoading: isUpdatingRecord } = useMutation('right_assignments:cancel');

  // cancelAssign
  const { writeContract: cancelAssign, isLoading: isCancelling } = useWriteContract({
    onSuccess: async () => {
      try {
        // 调用 mutation 更新数据库状态
        // 优先使用 transactionHash，如果没有则使用 assignId
        await cancelRightAssignment({
          id: currentRightAssignment?.id,
          assignId: currentRightAssignment?.assign_id,
          investmentIndex: assignor ? index : undefined,
          assignor,
        });

        onCancelSuccess();
      } catch (error) {
      }
    },
    onError: (error: Error) => {
      toast.error(t('investor.cancel_assign_failed') || 'Failed to cancel assign');
    },
  });

  return (
    <div className="card v-stack w-full gap-3">
      <div className="h-stack w-full items-center justify-between">
        <div className="h-stack items-center gap-2">
          <div className="bg-primary/20 rounded-lg p-1.5">
            <HandCoins size={16} className="text-primary" />
          </div>
          <Typography size="3" weight={600}>
            {t(isTeamLock ? 'investor.team_lock' : 'investor.investment')} #{index + 1}
          </Typography>
        </div>
        {!investment.isPendingOrEnded && (
          <Button
            size="xs"
            rounded="full"
            variant="contained"
            color="destructive"
            onClick={() => {
              navigate('Investment', { params: { index } });
            }}
          >
            <Typography size="2" weight={500} i18n="investor.transfer" />
          </Button>
        )}
        {investment.isPendingOrEnded && (
          <Button
            size="xs"
            rounded="full"
            variant="contained"
            color="destructive"
            isLoading={isCancelling || isUpdatingRecord}
            onClick={() => {
              // 从 pending right assignments 中获取 assignId
              if (!currentRightAssignment || !currentRightAssignment.assign_id) {
                toast.error(t('investor.cancel_assign_failed') || 'Failed to find right assignment');
                return;
              }

              cancelAssign({
                address: MOONINI_LOCK_ADDRESS,
                abi: MOONINI_LOCK_ABI,
                functionName: 'cancelAssign',
                args: [currentRightAssignment.assign_id],
              });
            }}
          >
            <Typography size="2" weight={500} i18n="investor.cancel_assign" />
          </Button>
        )}

      </div>

      <div className="v-stack w-full gap-2">
        <div className="h-stack w-full justify-between">
          <Typography size="2" color="rgba(255,255,255,0.54)">
            {t('investor.total_claimable')}
          </Typography>
          <div className="h-stack items-center gap-1">
            <Image src="/images/tokens/moonini.png" alt="moonini" width={20} height={20} />
            <Typography size="2" weight={500}>
              {formattedTotal} $MOONINI
            </Typography>
          </div>
        </div>
        <div className="h-stack w-full justify-between">
          <Typography size="2" color="rgba(255,255,255,0.54)">
            {t('investor.claimed')}
          </Typography>
          <div className="h-stack items-center gap-1">
            <Image src="/images/tokens/moonini.png" alt="moonini" width={20} height={20} />
            <Typography size="2" weight={500}>
              {formattedClaimed} $MOONINI
            </Typography>
          </div>
        </div>
        <div className="h-stack w-full justify-between">
          <Typography size="2" color="rgba(255,255,255,0.54)">
            {t('investor.remaining') || 'Remaining'}
          </Typography>
          <div className="h-stack items-center gap-1">
            <Image src="/images/tokens/moonini.png" alt="moonini" width={20} height={20} />
            <Typography size="2" weight={500} className="text-primary">
              {formattedRemaining} $MOONINI
            </Typography>
          </div>
        </div>
        <div className="w-full h-1.5 rounded-full overflow-hidden relative" style={{ backgroundColor: 'rgba(255, 255, 255, 0.1)' }}>
          <div
            className="h-full transition-all duration-300 ease-out rounded-full"
            style={{ width: `${progress}%`, backgroundColor: '#3b82f6' }}
          />
        </div>
        <div className="h-stack w-full justify-between">
          <Typography size="1" color="rgba(255,255,255,0.4)">
            {t('investor.start_date') || 'Start'}: {startDate.toLocaleDateString()}
          </Typography>
          <Typography size="1" color="rgba(255,255,255,0.4)">
            {t('investor.end_date') || 'End'}: {endDate.toLocaleDateString()}
          </Typography>
        </div>
      </div>
    </div>
  );
}
